import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  TextInput,
  Platform,
} from 'react-native';
import { InsetModal as Modal } from '../components/InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, ConfirmationModal } from '../components';
import { Button } from '../components/design-system';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';

// ─── Types ────────────────────────────────────────────────────────────────────

interface InventoryMovement {
  type: 'in' | 'out' | 'adjustment';
  quantity: number;
  reason?: string;
  referenceId?: string;
  note?: string;
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  orderNumber?: string;
  orderCreatedBy?: string;
}

interface InventoryRecord {
  _id: string;
  productId: string;
  variantId?: string;
  quantity: number;
  reservedQuantity: number;
  lowStockThreshold?: number;
  updatedAt: string;
  productName?: string;
  productSku?: string;
  movements?: InventoryMovement[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getStockColor = (r: InventoryRecord) => {
  if (r.quantity <= 0) return colors.error;
  if (r.lowStockThreshold !== undefined && r.quantity <= r.lowStockThreshold) return '#f59e0b';
  return '#10b981';
};

const getStockLabel = (r: InventoryRecord) => {
  if (r.quantity <= 0) return 'Out of stock';
  if (r.lowStockThreshold !== undefined && r.quantity <= r.lowStockThreshold) return 'Low stock';
  return 'In stock';
};

const FILTER_OPTIONS = ['all', 'in_stock', 'low_stock', 'out_of_stock'] as const;
type FilterOption = typeof FILTER_OPTIONS[number];

const FILTER_LABELS: Record<FilterOption, string> = {
  all:         'All',
  in_stock:    'In Stock',
  low_stock:   'Low Stock',
  out_of_stock:'Out of Stock',
};

// ─── Card ─────────────────────────────────────────────────────────────────────

const InventoryCard: React.FC<{
  record: InventoryRecord;
  onAdjust: () => void;
  onMovements: () => void;
}> = ({ record, onAdjust, onMovements }) => {
  const styles = useThemedStyles(getStyles);
  const stockColor = getStockColor(record);
  const stockLabel = getStockLabel(record);

  return (
    <View style={styles.card}>
      {/* Stock level indicator strip */}
      <View style={[styles.cardStrip, { backgroundColor: stockColor }]} />

      {/* Body */}
      <View style={styles.cardBody}>
        <View style={styles.cardTitleRow}>
          <Text style={styles.cardName} numberOfLines={1}>
            {record.productName || record.productId}
          </Text>
          <View style={[styles.statusBadge, { backgroundColor: stockColor + '20' }]}>
            <View style={[styles.statusDot, { backgroundColor: stockColor }]} />
            <Text style={[styles.statusBadgeText, { color: stockColor }]}>{stockLabel}</Text>
          </View>
        </View>

        <View style={styles.cardMeta}>
          {record.productSku ? (
            <Text style={styles.metaText}>SKU: {record.productSku}</Text>
          ) : null}
          {record.variantId ? (
            <Text style={styles.metaText}> · Variant</Text>
          ) : null}
        </View>

        <View style={styles.cardStats}>
          <View style={styles.statItem}>
            <Text style={[styles.statValue, { color: stockColor }]}>{record.quantity}</Text>
            <Text style={styles.statLabel}>In Stock</Text>
          </View>
          {record.reservedQuantity > 0 && (
            <View style={styles.statItem}>
              <Text style={styles.statValue}>{record.reservedQuantity}</Text>
              <Text style={styles.statLabel}>Reserved</Text>
            </View>
          )}
          {record.lowStockThreshold !== undefined && (
            <View style={styles.statItem}>
              <Text style={styles.statValue}>{record.lowStockThreshold}</Text>
              <Text style={styles.statLabel}>Alert at</Text>
            </View>
          )}
        </View>

        {/* Card action row */}
        <View style={styles.cardActions}>
          <TouchableOpacity
            style={styles.cardActionBtn}
            onPress={onMovements}
            activeOpacity={0.7}
          >
            <Ionicons name="list-outline" size={14} color={colors.primary} />
            <Text style={styles.cardActionText}>Movements</Text>
            {(record.movements?.length ?? 0) > 0 && (
              <View style={styles.movementCountBadge}>
                <Text style={styles.movementCountText}>{record.movements!.length}</Text>
              </View>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.cardActionBtn, styles.cardActionBtnPrimary]}
            onPress={onAdjust}
            activeOpacity={0.7}
          >
            <Ionicons name="swap-vertical-outline" size={14} color={colors.white} />
            <Text style={[styles.cardActionText, styles.cardActionTextPrimary]}>Adjust</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

// ─── Screen ───────────────────────────────────────────────────────────────────

export const InventoryScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const [showLeftPane]  = useState(Platform.OS === 'web');

  const [records, setRecords]       = useState<InventoryRecord[]>([]);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [total, setTotal]           = useState(0);
  const [error, setError]           = useState<string | null>(null);
  const [search, setSearch]         = useState('');
  const [filter, setFilter]         = useState<FilterOption>('all');

  // Adjust modal
  const [adjustTarget, setAdjustTarget] = useState<InventoryRecord | null>(null);
  const [adjustType, setAdjustType]     = useState<'in' | 'out' | 'adjustment'>('in');
  const [adjustQty, setAdjustQty]       = useState('');
  const [adjustNote, setAdjustNote]     = useState('');
  const [adjusting, setAdjusting]       = useState(false);

  // Movements modal
  const [movementsTarget, setMovementsTarget] = useState<InventoryRecord | null>(null);

  const load = useCallback(async (reset = false) => {
    try {
      setError(null);
      if (reset) setLoading(true);
      const lowStockOnly = filter === 'low_stock';
      const res = await apiService.getInventory({ lowStockOnly, limit: 300 });
      if (res.statusCode === 1000 && res.data) {
        setRecords(res.data as InventoryRecord[]);
        setTotal((res as any).additionalInfo?.total ?? res.data.length);
      } else {
        setError(res.errors?.[0]?.message || 'Failed to load inventory');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter]);

  useEffect(() => { load(true); }, [load]);

  const handleRefresh = () => { setRefreshing(true); load(false); };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  // Client-side filter + search
  const filtered = records.filter(r => {
    if (filter === 'out_of_stock' && r.quantity > 0) return false;
    if (filter === 'in_stock' && (r.quantity <= 0 || (r.lowStockThreshold !== undefined && r.quantity <= r.lowStockThreshold))) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !r.productId.toLowerCase().includes(q) &&
        !(r.productName || '').toLowerCase().includes(q) &&
        !(r.productSku  || '').toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  const openAdjust = (record: InventoryRecord) => {
    setAdjustTarget(record);
    setAdjustType('in');
    setAdjustQty('');
    setAdjustNote('');
  };

  const openMovements = (record: InventoryRecord) => {
    setMovementsTarget(record);
  };

  const handleAdjust = async () => {
    if (!adjustTarget) return;
    const qty = parseInt(adjustQty, 10);
    if (!qty || qty <= 0) {
      showNotification('error', 'Error', 'Enter a valid quantity');
      return;
    }
    try {
      setAdjusting(true);
      const res = await apiService.adjustInventory(adjustTarget.productId, {
        type:      adjustType,
        quantity:  qty,
        note:      adjustNote || undefined,
        variantId: adjustTarget.variantId,
      });
      if (res.statusCode === 1000) {
        showNotification('success', 'Updated', 'Inventory adjusted successfully');
        setAdjustTarget(null);
        load(false);
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to adjust inventory');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message || 'Failed to adjust inventory');
    } finally {
      setAdjusting(false);
    }
  };

  // ── List content ─────────────────────────────────────────────────────────────

  const renderListContent = () => {
    if (loading && records.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centerText}>Loading inventory...</Text>
        </View>
      );
    }

    if (error && records.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="alert-circle-outline" size={64} color={colors.error} />
          <Text style={styles.errorTitle}>Unable to Load</Text>
          <Text style={styles.errorText}>{error}</Text>
          <Button title="Try Again" onPress={() => load(true)} variant="primary" style={styles.retryBtn} />
        </View>
      );
    }

    if (records.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="cube-outline" size={64} color={colors.border} />
          <Text style={styles.errorTitle}>No inventory records</Text>
          <Text style={styles.errorText}>
            Products with inventory tracking enabled will automatically appear here when added or ordered.
          </Text>
        </View>
      );
    }

    if (filtered.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="search-outline" size={48} color={colors.border} />
          <Text style={styles.errorTitle}>No results</Text>
          <Text style={styles.errorText}>Try adjusting your search or filter.</Text>
        </View>
      );
    }

    return (
      <FlatList
        data={filtered}
        keyExtractor={r => r._id}
        renderItem={({ item }) => (
          <InventoryCard
            record={item}
            onAdjust={() => openAdjust(item)}
            onMovements={() => openMovements(item)}
          />
        )}
        contentContainerStyle={styles.listContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.primary} />}
        ItemSeparatorComponent={() => <View style={styles.separator} />}
      />
    );
  };

  // ── Movements modal ───────────────────────────────────────────────────────────

  const renderMovementsModal = () => {
    const movTypeIcon = (type: string) =>
      type === 'in' ? 'arrow-down-circle' : type === 'out' ? 'arrow-up-circle' : 'refresh-circle';
    const movTypeColor = (type: string) =>
      type === 'in' ? '#10b981' : type === 'out' ? colors.error : '#f59e0b';

    const movements = movementsTarget?.movements ?? [];
    const sorted = [...movements].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    return (
      <Modal
        visible={!!movementsTarget}
        transparent
        animationType="slide"
        onRequestClose={() => setMovementsTarget(null)}
      >
        <View style={Platform.OS === 'web' ? styles.modalWebOverlay : styles.modalNativeContainer}>
          <TouchableOpacity
            style={Platform.OS === 'web' ? styles.modalBackdrop : styles.modalNativeBackdrop}
            activeOpacity={1}
            onPress={() => setMovementsTarget(null)}
          />
          <View style={Platform.OS === 'web' ? styles.modalWebContainer : undefined}>
            <View style={[styles.modalSheet, styles.movementsSheet]}>
              <View style={styles.modalHandle} />

              <View style={styles.modalHeader}>
                <View>
                  <Text style={styles.modalTitle}>Movements</Text>
                  {movementsTarget && (
                    <Text style={styles.modalSubtitle} numberOfLines={1}>
                      {movementsTarget.productName || movementsTarget.productId}
                    </Text>
                  )}
                </View>
                <TouchableOpacity onPress={() => setMovementsTarget(null)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <Ionicons name="close" size={22} color={colors.textLight} />
                </TouchableOpacity>
              </View>

              {sorted.length === 0 ? (
                <View style={styles.movEmptyContainer}>
                  <Ionicons name="list-outline" size={40} color={colors.border} />
                  <Text style={styles.movEmptyText}>No movements recorded yet.</Text>
                </View>
              ) : (
                <FlatList
                  data={sorted}
                  keyExtractor={(_, i) => String(i)}
                  style={styles.movList}
                  renderItem={({ item: mov }) => {
                    const typeColor = movTypeColor(mov.type);
                    const typeIcon  = movTypeIcon(mov.type) as any;
                    const date = new Date(mov.createdAt);
                    const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                    const timeStr = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
                    const isOrderMovement = mov.referenceId && (
                      mov.reason === 'order_placed' ||
                      mov.reason === 'order_cancelled' ||
                      mov.reason === 'order_edited'
                    );
                    return (
                      <View style={styles.movRow}>
                        <View style={[styles.movIconWrap, { backgroundColor: typeColor + '18' }]}>
                          <Ionicons name={typeIcon} size={20} color={typeColor} />
                        </View>
                        <View style={styles.movContent}>
                          <View style={styles.movTopRow}>
                            <Text style={[styles.movQty, { color: typeColor }]}>
                              {mov.type === 'in' ? '+' : mov.type === 'out' ? '-' : '='}{mov.quantity}
                            </Text>
                            <Text style={styles.movDate}>{dateStr} · {timeStr}</Text>
                          </View>
                          {mov.reason && (
                            <Text style={styles.movReason}>
                              {mov.reason.replace(/_/g, ' ')}
                            </Text>
                          )}
                          {mov.note && !isOrderMovement && (
                            <Text style={styles.movNote}>{mov.note}</Text>
                          )}
                          {/* Order-linked info */}
                          {isOrderMovement && (
                            <View style={styles.movOrderRow}>
                              {mov.orderCreatedBy ? (
                                <Text style={styles.movUser}>
                                  <Ionicons name="person-outline" size={11} color={colors.textLight} /> {mov.orderCreatedBy}
                                </Text>
                              ) : null}
                              <TouchableOpacity
                                style={styles.movOrderBtn}
                                activeOpacity={0.7}
                                onPress={() => {
                                  setMovementsTarget(null);
                                  navigateTo('order-edit', mov.referenceId);
                                }}
                              >
                                <Ionicons name="receipt-outline" size={12} color={colors.primary} />
                                <Text style={styles.movOrderBtnText}>
                                  {mov.orderNumber || 'Open Order'}
                                </Text>
                              </TouchableOpacity>
                            </View>
                          )}
                          {/* Manual movement user */}
                          {!isOrderMovement && mov.createdByName && (
                            <Text style={styles.movUser}>
                              <Ionicons name="person-outline" size={11} color={colors.textLight} /> {mov.createdByName}
                            </Text>
                          )}
                        </View>
                      </View>
                    );
                  }}
                  ItemSeparatorComponent={() => <View style={styles.movDivider} />}
                />
              )}
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // ── Adjust modal ─────────────────────────────────────────────────────────────

  const renderAdjustModal = () => (
    <Modal
      visible={!!adjustTarget}
      transparent
      animationType="slide"
      onRequestClose={() => setAdjustTarget(null)}
    >
      <View style={Platform.OS === 'web' ? styles.modalWebOverlay : styles.modalNativeContainer}>
        <TouchableOpacity
          style={Platform.OS === 'web' ? styles.modalBackdrop : styles.modalNativeBackdrop}
          activeOpacity={1}
          onPress={() => setAdjustTarget(null)}
        />
        <View style={Platform.OS === 'web' ? styles.modalWebContainer : undefined}>
        <View style={styles.modalSheet}>
          <View style={styles.modalHandle} />

          <View style={styles.modalHeader}>
            <View>
              <Text style={styles.modalTitle}>Adjust Stock</Text>
              {adjustTarget && (
                <Text style={styles.modalSubtitle} numberOfLines={1}>
                  {adjustTarget.productName || adjustTarget.productId}
                </Text>
              )}
            </View>
            <TouchableOpacity onPress={() => setAdjustTarget(null)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="close" size={22} color={colors.textLight} />
            </TouchableOpacity>
          </View>

          <Text style={styles.fieldLabel}>Movement type</Text>
          <View style={styles.typeRow}>
            {(['in', 'out', 'adjustment'] as const).map(t => (
              <TouchableOpacity
                key={t}
                style={[styles.typeChip, adjustType === t && styles.typeChipActive]}
                onPress={() => setAdjustType(t)}
                activeOpacity={0.7}
              >
                <Ionicons
                  name={t === 'in' ? 'add-circle-outline' : t === 'out' ? 'remove-circle-outline' : 'refresh-outline'}
                  size={14}
                  color={adjustType === t ? colors.white : colors.textLight}
                />
                <Text style={[styles.typeChipText, adjustType === t && styles.typeChipTextActive]}>
                  {t === 'in' ? 'Stock In' : t === 'out' ? 'Stock Out' : 'Set Exact'}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={styles.fieldLabel}>
            {adjustType === 'adjustment' ? 'New quantity' : 'Quantity'}
          </Text>
          <TextInput
            style={styles.input}
            value={adjustQty}
            onChangeText={setAdjustQty}
            keyboardType="numeric"
            placeholder="0"
            placeholderTextColor={colors.textLight}
          />

          <Text style={styles.fieldLabel}>Note (optional)</Text>
          <TextInput
            style={[styles.input, styles.noteInput]}
            value={adjustNote}
            onChangeText={setAdjustNote}
            placeholder="e.g. Received new shipment"
            placeholderTextColor={colors.textLight}
            multiline
          />

          <Button
            title={adjusting ? 'Saving...' : 'Save Adjustment'}
            onPress={handleAdjust}
            variant="primary"
            disabled={adjusting}
            style={styles.saveBtn}
          />
        </View>
        </View>
      </View>
    </Modal>
  );

  // ── Main ─────────────────────────────────────────────────────────────────────

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      showLeftPane={showLeftPane}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
    >
      <View style={styles.container}>
        {/* Page header */}
        <View style={styles.pageHeader}>
          <View style={styles.pageHeaderContent}>
            <View style={styles.pageHeaderLeft}>
              <Text style={styles.pageTitle}>Inventory</Text>
              {!loading && (
                <Text style={styles.pageSubtitle}>
                  {total} {total === 1 ? 'product' : 'products'} tracked
                </Text>
              )}
            </View>
          </View>
          <Text style={styles.pageHint}>
            Monitor stock levels and adjust quantities for your products.
          </Text>

          {/* Search */}
          <View style={styles.searchBox}>
            <Ionicons name="search-outline" size={16} color={colors.textLight} />
            <TextInput
              style={styles.searchInput}
              value={search}
              onChangeText={setSearch}
              placeholder="Search by product name or SKU..."
              placeholderTextColor={colors.textLight}
            />
            {search ? (
              <TouchableOpacity onPress={() => setSearch('')}>
                <Ionicons name="close-circle" size={16} color={colors.textLight} />
              </TouchableOpacity>
            ) : null}
          </View>

          {/* Filter chips */}
          <FlatList
            horizontal
            showsHorizontalScrollIndicator={false}
            data={FILTER_OPTIONS}
            keyExtractor={t => t}
            contentContainerStyle={styles.filterRow}
            renderItem={({ item: t }) => {
              const active = filter === t;
              return (
                <TouchableOpacity
                  style={[styles.filterChip, active && styles.filterChipActive]}
                  onPress={() => setFilter(t)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.filterChipText, active && styles.filterChipTextActive]}>
                    {FILTER_LABELS[t]}
                  </Text>
                </TouchableOpacity>
              );
            }}
          />
        </View>

        <View style={styles.listContainer}>{renderListContent()}</View>
      </View>

      {renderMovementsModal()}
      {renderAdjustModal()}
    </ScreenLayout>
  );
};

export default InventoryScreen;

// ─── Styles ───────────────────────────────────────────────────────────────────

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.lightGray },

  // ── Page header ──────────────────────────────────────────────────────────────
  pageHeader: {
    backgroundColor: colors.background.surface,
    paddingTop: spacing.lg,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    ...designTokens.shadows.sm,
  },
  pageHeaderContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  pageHeaderLeft: { flex: 1 },
  pageTitle: {
    fontSize: typography.fontSize.xxl,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
    marginBottom: spacing.xs,
  },
  pageSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  pageHint: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginTop: spacing.xs,
    marginBottom: spacing.sm,
  },

  // ── Search ───────────────────────────────────────────────────────────────────
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.lightGray,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    marginBottom: spacing.sm,
    gap: spacing.xs,
    borderWidth: 1,
    borderColor: colors.border,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
    padding: 0,
  },

  // ── Filters ──────────────────────────────────────────────────────────────────
  filterRow: {
    paddingVertical: spacing.xs,
    gap: spacing.xs,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
    marginRight: spacing.xs,
  },
  filterChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  filterChipText: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.medium,
  },
  filterChipTextActive: { color: colors.white },

  // ── List ─────────────────────────────────────────────────────────────────────
  listContainer: { flex: 1 },
  listContent: {
    padding: spacing.md,
    paddingBottom: Platform.OS !== 'web' ? 100 : spacing.lg,
  },
  separator: { height: spacing.sm },

  // ── Card ─────────────────────────────────────────────────────────────────────
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    overflow: 'hidden',
    ...designTokens.shadows.sm,
  },
  cardStrip: {
    width: 4,
    alignSelf: 'stretch',
  },
  cardBody: {
    flex: 1,
    padding: spacing.md,
    gap: 4,
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexWrap: 'wrap',
  },
  cardName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    flexShrink: 1,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 10,
  },
  statusDot: {
    width: 5,
    height: 5,
    borderRadius: 3,
  },
  statusBadgeText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
  },
  cardMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  cardStats: {
    flexDirection: 'row',
    gap: spacing.lg,
    marginTop: spacing.xs,
  },
  statItem: { alignItems: 'flex-start' },
  statValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
    lineHeight: 26,
  },
  statLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  cardActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  cardActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 5,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: colors.background.surface,
  },
  cardActionBtnPrimary: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  cardActionText: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  cardActionTextPrimary: {
    color: colors.white,
  },
  movementCountBadge: {
    backgroundColor: colors.primary,
    borderRadius: 8,
    minWidth: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  movementCountText: {
    fontSize: 10,
    color: colors.white,
    fontFamily: typography.fontFamily.medium,
  },

  // ── Movements modal ───────────────────────────────────────────────────────────
  movementsSheet: {
    maxHeight: Platform.OS !== 'web' ? '80%' : undefined,
  },
  movList: {
    maxHeight: Platform.OS !== 'web' ? undefined : 400,
  },
  movEmptyContainer: {
    alignItems: 'center',
    paddingVertical: spacing.xxl,
    gap: spacing.sm,
  },
  movEmptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  movRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
  },
  movIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  movContent: {
    flex: 1,
    gap: 2,
  },
  movTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.xs,
  },
  movQty: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.bold,
  },
  movDate: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  movReason: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    textTransform: 'capitalize',
  },
  movNote: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    fontStyle: 'italic',
  },
  movUser: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginTop: 2,
  },
  movDivider: {
    height: 1,
    backgroundColor: colors.border,
    marginHorizontal: spacing.xs,
  },
  movOrderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: 3,
    flexWrap: 'wrap',
  },
  movOrderBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: colors.primary + '0D',
  },
  movOrderBtnText: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },

  // ── Empty / error states ──────────────────────────────────────────────────────
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxl,
  },
  centerText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  errorTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.textDark,
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
    fontFamily: typography.fontFamily.semibold,
    textAlign: 'center',
  },
  errorText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    textAlign: 'center',
    marginBottom: spacing.xl,
    fontFamily: typography.fontFamily.regular,
  },
  retryBtn: { minWidth: 180 },

  // ── Adjust modal ─────────────────────────────────────────────────────────────
  modalNativeContainer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  modalNativeBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  modalWebOverlay: {
    flex: 1,
    position: 'relative',
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0, bottom: 0, left: 0, right: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 1,
  },
  modalWebContainer: {
    position: 'absolute',
    top: 180,
    left: 355,
    right: 355,
    bottom: 0,
    zIndex: 2,
    justifyContent: 'flex-end',
  },
  modalSheet: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: designTokens.borderRadius.xl,
    borderTopRightRadius: designTokens.borderRadius.xl,
    padding: spacing.lg,
    paddingBottom: Platform.OS !== 'web' ? 36 : spacing.lg,
    gap: spacing.sm,
  },
  modalHandle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.border,
    alignSelf: 'center',
    marginBottom: spacing.sm,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.xs,
  },
  modalTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  modalSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginTop: 2,
  },
  fieldLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.medium,
    marginTop: spacing.xs,
  },
  typeRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    flexWrap: 'wrap',
  },
  typeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  typeChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  typeChipText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.medium,
  },
  typeChipTextActive: {
    color: colors.white,
  },
  input: {
    backgroundColor: colors.lightGray,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
  },
  noteInput: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  saveBtn: {
    marginTop: spacing.sm,
  },
});
